
import React, { useRef, useEffect, useCallback } from 'react';
import { ViewState } from '../types';
import { RefreshCw, Square, ChevronLeft, ChevronRight, Zap } from 'lucide-react';

// LOGO PARA BOTÓN DE AGENTE
const AppLogo = ({ size = 24, className = "", monochrome = false }: { size?: number, className?: string, monochrome?: boolean }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <rect x="2" y="2" width="9" height="9" rx="2.5" className={monochrome ? "fill-current" : "fill-blue-600"} />
    <rect x="13" y="2" width="9" height="9" rx="2.5" className={monochrome ? "fill-current opacity-40" : "fill-blue-50"} />
    <rect x="2" y="13" width="9" height="9" rx="2.5" className={monochrome ? "fill-current opacity-70" : "fill-indigo-600"} />
    <rect x="13" y="13" width="9" height="9" rx="2.5" className={monochrome ? "fill-current opacity-90" : "fill-purple-500"} />
  </svg>
);

interface NavItem {
  id: ViewState;
  label: string;
  icon: any;
}

interface BottomNavProps {
  currentView: ViewState;
  onNavigate: (view: ViewState) => void;
  items?: NavItem[];
  isDarkMode?: boolean;
  isAgentRecording?: boolean;
  isAgentProcessing?: boolean;
  onToggleAgent?: () => void;
  notifications?: Record<string, boolean>; // Nueva prop para badges
}

const BottomNav: React.FC<BottomNavProps> = ({ 
  currentView, 
  onNavigate, 
  items = [], 
  isDarkMode,
  isAgentRecording = false,
  isAgentProcessing = false,
  onToggleAgent,
  notifications = {}
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLButtonElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sincronizar scroll con el item activo para que siempre esté visible
  useEffect(() => {
    if (activeItemRef.current && scrollRef.current) {
      activeItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [currentView]);

  const isViewActive = (id: ViewState) => {
    if (id === ViewState.REPORTS) {
      return [
        ViewState.REPORTS, ViewState.PHRASES, ViewState.CREATE_REPORT, 
        ViewState.EDIT_REPORT, ViewState.CREATE_PHRASE, ViewState.EDIT_PHRASE, 
        ViewState.MANAGE_FILTERS, ViewState.RECYCLE_BIN
      ].includes(currentView);
    }
    return currentView === id;
  };

  const scrollNav = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -150 : 150;
      // Quitamos momentáneamente el scroll-snap para permitir el scroll libre via flechas
      scrollRef.current.style.scrollSnapType = 'none';
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      // Restauramos el snap tras un breve delay para que la inercia termine
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.style.scrollSnapType = 'x mandatory';
      }, 500);
    }
  };

  const handlePopOut = useCallback((viewId: ViewState) => {
    const width = 414;
    const height = 896;
    const left = window.screen.width - width - 50;
    const top = 50;
    const timestamp = Date.now();
    
    window.open(
      `${window.location.origin}${window.location.pathname}?layout=single&view=${viewId}`,
      `MODULAR_WIN_${viewId}_${timestamp}`,
      `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=no,location=no`
    );
  }, []);

  const startPress = (viewId: ViewState) => {
    longPressTimer.current = setTimeout(() => {
      handlePopOut(viewId);
      if (navigator.vibrate) navigator.vibrate(50);
    }, 1500); 
  };

  const cancelPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  return (
    <div className={`w-full h-full border-t z-50 flex items-center px-2 transition-colors duration-300 backdrop-blur-md ${isDarkMode ? 'bg-[#1e2024]/95 border-gray-800' : 'bg-white/95 border-gray-100'}`}>
      
      {/* BOTÓN AGENTE (FIJO) */}
      <button 
        onClick={(e) => {
          e.stopPropagation();
          onToggleAgent?.();
        }}
        disabled={isAgentProcessing}
        className={`relative flex flex-col items-center justify-center shrink-0 w-[58px] h-[68px] rounded-2xl transition-all active:scale-90 z-[60] ${
          isAgentRecording 
            ? 'bg-red-500 text-white shadow-[0_0_20px_rgba(239,68,68,0.5)]' 
            : isAgentProcessing 
              ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-500' 
              : (isDarkMode ? 'text-indigo-400 bg-indigo-400/8' : 'text-indigo-600 bg-indigo-50')
        }`}
      >
        <div className="relative mb-0.5">
          {isAgentRecording && (
            <div className="absolute -inset-2 rounded-full border-2 border-white/50 animate-ping z-0" />
          )}
          <div className="relative z-10">
            {isAgentProcessing ? (
              <RefreshCw size={22} className="animate-spin" />
            ) : isAgentRecording ? (
              <Square size={20} fill="currentColor" />
            ) : (
              <Zap size={22} />
            )}
          </div>
        </div>
        {(isAgentProcessing || isAgentRecording) && (
          <span className={`text-[7px] font-black uppercase tracking-widest leading-none ${isAgentRecording ? 'text-white' : 'opacity-70'}`}>
            {isAgentProcessing ? 'CARGA' : 'LIVE'}
          </span>
        )}
      </button>

      <div className={`h-8 w-px mx-1 shrink-0 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}></div>

      {/* CONTENEDOR DE CINTA CON FLECHAS */}
      <div className="relative flex-1 h-full flex items-center overflow-hidden">
        {/* Flecha Izquierda */}
        <button 
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); scrollNav('left'); }}
          className={`absolute left-0 z-[70] p-2 rounded-full shadow-xl transition-all active:scale-75 ${isDarkMode ? 'bg-gray-800/95 text-blue-400 border border-gray-700' : 'bg-white/95 text-blue-600 border border-gray-100'}`}
        >
          <ChevronLeft size={16} strokeWidth={3} />
        </button>

        {/* CINTA DE NAVEGACIÓN HORIZONTAL */}
        <div 
          ref={scrollRef}
          className="flex-1 flex items-center gap-1 overflow-x-auto no-scrollbar py-2 px-8 h-full scroll-smooth snap-x snap-mandatory"
        >
          {items.map((item) => {
            const isActive = isViewActive(item.id);
            const hasNotification = notifications[item.id];
            
            return (
              <button 
                key={item.id}
                ref={isActive ? activeItemRef : null}
                onClick={() => onNavigate(item.id)}
                onPointerDown={() => startPress(item.id)}
                onPointerUp={cancelPress}
                onPointerLeave={cancelPress}
                onContextMenu={(e) => e.preventDefault()}
                className={`
                  flex flex-col items-center justify-center shrink-0
                  transition-all select-none h-[68px] min-w-[65px] rounded-2xl
                  snap-center touch-pan-x relative
                  ${isActive 
                    ? (isDarkMode ? 'text-blue-400 bg-blue-400/12 shadow-inner border border-blue-400/20' : 'text-blue-600 bg-blue-50 border border-blue-100') 
                    : (isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600')}
                `}
              >
                <div className={`transition-all duration-300 ${isActive ? 'scale-110 mb-0.5' : 'scale-100 mb-1 opacity-70'} relative`}>
                  <item.icon size={22} strokeWidth={isActive ? 2.5 : 2} />
                  {/* Badge de Notificación */}
                  {hasNotification && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white dark:border-gray-900 animate-in zoom-in duration-300 shadow-sm" />
                  )}
                </div>
                <span className={`text-[8px] font-black uppercase tracking-tighter truncate w-full text-center px-1 leading-none ${isActive ? 'opacity-100' : 'opacity-50'} ${hasNotification ? 'text-red-500 opacity-100 font-bold' : ''}`}>
                  {item.label.split(' ')[0]}
                </span>
              </button>
            );
          })}
          <div className="shrink-0 w-8 h-4"></div>
        </div>

        {/* Flecha Derecha */}
        <button 
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); scrollNav('right'); }}
          className={`absolute right-0 z-[70] p-2 rounded-full shadow-xl transition-all active:scale-75 ${isDarkMode ? 'bg-gray-800/95 text-blue-400 border border-gray-700' : 'bg-white/95 text-blue-600 border border-gray-100'}`}
        >
          <ChevronRight size={16} strokeWidth={3} />
        </button>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default BottomNav;
